import React, { useState, useEffect, useMemo, useRef } from 'react'
import DynamicHeadphones from './customColours/DynamicHeadphones'
import { IProduct, ICustomComponent } from '@/context/ProductDataStore'
import useProductDataStore from '@/context/ProductDataStore'
import useBasketStore from '@/context/BasketStore'
import { editableParts, surpriseColours } from './customColours/configuratorLayers'
import { Dices, X } from 'lucide-react'

interface CustomColoursContainerProps {
  open: boolean
  customColourProduct: IProduct | undefined
  customComponents: ICustomComponent[]
  updateCustomComponent: (componentName: string, materialId: string, position: string | null) => void
  onClose: () => void
  onComplete: () => void
  navHeight?: number
  basketItemId?: string
}

type PartRef = { name: string; position: string | null }
type ColourChange = PartRef & { materialId: string }

const sideLabel = (position: string | null) => (position ? position.charAt(0).toUpperCase() + position.slice(1) : '')

/**
 * Full-screen custom colours editor: the headphones on top, then a colour tray for the
 * selected part and a strip of every part that can change colour (tap a part, its marker
 * or its chip to select it).
 */
const CustomColoursContainer: React.FC<CustomColoursContainerProps> = ({
  open,
  customColourProduct,
  customComponents,
  updateCustomComponent,
  onClose,
  onComplete,
  basketItemId,
}) => {
  const [selected, setSelected] = useState<PartRef | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const materialInformation = useProductDataStore(state => state.materialInformation)
  const { updateBasketItem } = useBasketStore()
  const trayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      // Lock the scrollable container
      const scrollContainer = document.querySelector('.overflow-y-auto')
      if (scrollContainer) {
        scrollContainer.classList.add('overflow-hidden')
      }
      window.scrollTo(0, 0)
    } else {
      document.body.style.overflow = ''
      // Unlock the scrollable container
      const scrollContainer = document.querySelector('.overflow-y-auto')
      if (scrollContainer) {
        scrollContainer.classList.remove('overflow-hidden')
      }
    }

    return () => {
      document.body.style.overflow = ''
      // Ensure we unlock the scrollable container on cleanup
      const scrollContainer = document.querySelector('.overflow-y-auto')
      if (scrollContainer) {
        scrollContainer.classList.remove('overflow-hidden')
      }
    }
  }, [open])

  const parts = useMemo(() => editableParts(customComponents, materialInformation), [customComponents, materialInformation])
  const current = parts.find(part => selected && part.name === selected.name && part.position === selected.position)
  const materialName = (id: string) => materialInformation.find(m => m.materialId === id)?.kibu_name || 'Colour'

  // Open on the first part so the colours are visible straight away (once per opening, so
  // deselecting to see the whole headphones sticks); forget the selection on close.
  const autoSelected = useRef(false)
  useEffect(() => {
    if (!open) {
      autoSelected.current = false
      setSelected(null)
    } else if (!autoSelected.current && parts.length) {
      autoSelected.current = true
      setSelected({ name: parts[0].name, position: parts[0].position })
    }
  }, [open, parts])

  // Escape clears the selection too.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setSelected(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Keep the chosen swatch in view when switching parts.
  useEffect(() => {
    trayRef.current?.querySelector('[aria-checked="true"]')?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [current?.key])

  const selectPart = (partName: string, _mousePos: { x: number; y: number }, position: string | null) => {
    // A changeable part selects it; anything else (empty space, a fixed part, the background)
    // clears the selection so the whole headphones can be seen without the highlight.
    const part = parts.find(p => p.name === partName && p.position === position)
    setSelected(part ? { name: part.name, position: part.position } : null)
  }

  /** Apply colour changes to the editor, the product data and (when editing one) the basket item. */
  const applyChanges = (changes: ColourChange[]) => {
    if (!customColourProduct || !changes.length) return
    const changeFor = (comp: ICustomComponent) => changes.find(c => c.name === comp.name && c.position === comp.position)
    const updatedComponents = customComponents.map(comp => {
      const change = changeFor(comp)
      return change ? { ...comp, chosenMaterial: { $oid: change.materialId } } : comp
    })
    changes.forEach(change => updateCustomComponent(change.name, change.materialId, change.position))

    const { product, options, setProductData, materialInformation: info } = useProductDataStore.getState()
    if (product && options) {
      const updatedOptions = options.map(option => ({
        ...option,
        options: option.options.map(opt => (opt._id === customColourProduct._id ? { ...opt, components: updatedComponents } : opt)),
      }))
      setProductData(product, updatedOptions, info)
    }

    if (basketItemId) {
      try {
        updateBasketItem(basketItemId, { colourWay: { ...customColourProduct, components: updatedComponents } })
      } catch (error) {
        console.error('customColourBasketCheck: Error updating basket item', error)
      }
    }
  }

  const handleClose = () => {
    setShowConfirmDialog(true)
  }

  const handleConfirmClose = () => {
    setShowConfirmDialog(false)
    onClose()
  }

  const handleCancelClose = () => {
    setShowConfirmDialog(false)
  }

  const handleComplete = () => {
    // Update the product store with the final components
    const { product, options, setProductData, materialInformation: info } = useProductDataStore.getState()
    if (product && options && customColourProduct) {
      const updatedOptions = options.map(option => ({
        ...option,
        options: option.options.map(opt => (opt._id === customColourProduct._id ? { ...opt, components: customComponents } : opt)),
      }))
      setProductData(product, updatedOptions, info)
    }

    // If this is being used to edit a basket item, update it one final time
    if (basketItemId && customColourProduct) {
      try {
        updateBasketItem(basketItemId, { colourWay: { ...customColourProduct, components: customComponents } })
      } catch (error) {
        console.error('customColourBasketCheck: Error updating basket item on complete', error)
      }
    }

    onComplete()
  }

  if (customColourProduct === undefined) {
    return null
  }

  return (
    <div className={`fixed w-screen h-screen custom-colours-container top-0 left-0 transition-bg flex items-center justify-center z-[9999] overflow-hidden
      ${open ? 'bg-black/10 duration-500 ease-hypereaseout max-h-none' : 'bg-transparent max-h-0 duration-200 ease-kibushopdefault pointer-events-none'}`}
      style={{ position: 'fixed', top: 0, left: 0, height: '100dvh' }}>
      {/* Tapping the background (not a part, marker, colour or button) clears the selection. */}
      <div onClick={event => { if (!(event.target as Element).closest('button, canvas, .colour-picker-container')) setSelected(null) }}
        className={`relative flex w-full h-full flex-col bg-custom-colours-background/80 backdrop-blur-xl overflow-hidden
          ${open ? 'opacity-100 scale-100 duration-[300ms] delay-[300ms] ease-kibushopdefault' : 'opacity-0 scale-120 duration-100 ease-kibushopdefault pointer-events-none'}`}>
        <header className="relative z-[110] flex items-center justify-between gap-3 px-4 pt-4 md:px-8 md:pt-6">
          <button
            type="button"
            aria-label="Close custom colours"
            onClick={handleClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-black transition-transform duration-200 ease-kibu hover:scale-105">
            <X className="h-5 w-5" />
          </button>
          <p className="font-headline text-base md:text-lg">Custom colours</p>
          <button
            type="button"
            onClick={handleComplete}
            className="rounded-full bg-orange px-5 py-2.5 font-headline leading-none text-white transition-transform duration-200 ease-kibu hover:scale-105 md:text-lg">
            Done
          </button>
        </header>

        <div className="relative min-h-0 flex-1 md:px-16">
          <DynamicHeadphones
            className={`${open ? '' : 'pointer-events-none'}`}
            viewName='front_view'
            engraving=''
            toggleColourPicker={selectPart}
            customColourProduct={customColourProduct}
            customComponents={customComponents}
            selectedPart={selected}
            showMarkers
            showHint={false}
            interactive
          />
        </div>

        {parts.length > 0 && (
          <section aria-label="Choose colours" className="colour-picker-container relative z-[110] mx-auto w-full max-w-xl rounded-t-3xl bg-white pb-[max(12px,env(safe-area-inset-bottom))] pt-4 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] md:mb-6 md:rounded-3xl">
            <div className="flex items-baseline justify-between gap-3 px-5">
              <p className="font-headline text-base text-black" aria-live="polite">
                {current ? <>{current.name}{current.position && <span className="text-black/50"> · {sideLabel(current.position)}</span>}</> : 'Your headphones'}
              </p>
              {current && <p className="truncate text-sm text-black/60">{materialName(current.chosen)}</p>}
            </div>

            <div ref={trayRef} role="radiogroup" aria-label={current ? `${current.name} colours` : 'Colours'}
              className="flex snap-x gap-3 overflow-x-auto px-5 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {!current && <p className="flex h-11 items-center text-sm text-black/60">Tap a part, or choose one below, to change its colour.</p>}
              {current?.colours.map(materialId => {
                const info = materialInformation.find(m => m.materialId === materialId)
                const chosen = current.chosen === materialId
                return (
                  <button
                    key={materialId}
                    type="button"
                    role="radio"
                    aria-checked={chosen}
                    aria-label={info?.kibu_name || 'Colour'}
                    title={info?.kibu_name}
                    onClick={() => applyChanges([{ name: current.name, position: current.position, materialId }])}
                    className={`h-11 w-11 shrink-0 snap-center rounded-full border border-black/10 transition-transform duration-150 ease-kibu
                      ${chosen ? 'scale-105 outline outline-2 outline-offset-2 outline-black' : 'hover:scale-105'}`}
                    style={{ backgroundColor: info?.hex_code }}
                  />
                )
              })}
            </div>

            <div className="flex gap-1 overflow-x-auto border-t border-black/5 px-3 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {parts.map(part => {
                const active = current?.key === part.key
                const colour = materialInformation.find(m => m.materialId === part.chosen)?.hex_code
                return (
                  <button
                    key={part.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setSelected({ name: part.name, position: part.position })}
                    className={`flex w-[76px] shrink-0 flex-col items-center gap-1.5 rounded-2xl px-1 py-2 text-center text-xs leading-tight transition-colors
                      ${active ? 'bg-black/5 text-black' : 'text-black/60 hover:text-black'}`}>
                    <span className={`h-8 w-8 rounded-full border ${active ? 'border-black' : 'border-black/10'}`} style={{ backgroundColor: colour }} />
                    <span>{part.name}{part.position && <span className="block text-black/50">{sideLabel(part.position)}</span>}</span>
                  </button>
                )
              })}
              <button
                type="button"
                onClick={() => applyChanges(surpriseColours(parts))}
                className="flex w-[76px] shrink-0 flex-col items-center gap-1.5 rounded-2xl px-1 py-2 text-center text-xs leading-tight text-black/60 transition-colors hover:text-black">
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-black/5"><Dices className="h-4 w-4" /></span>
                Surprise me
              </button>
            </div>
          </section>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold mb-4">Are you sure?</h3>
              <p className="text-gray-600 mb-6">
                If you close now, your custom colour selections will be lost and the first colourway will be selected instead.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => handleCancelClose()}
                  className="flex items-center p-2 md:pl-4 md:pr-4 w-max justify-center gap-1 w-min-content rounded-md font-headline text-black/50 leading-none
                    transition-all ease-kibu duration-200 items-center cursor-pointer border border-2 border-black/50
                    hover:bg-black hover:rounded-lg hover:brightness-105 hover:border-black/10 hover:scale-[1.02] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleConfirmClose()}
                  className={`flex items-center p-2 md:pl-4 md:pr-4 bg-orange justify-center w-min-content rounded-md font-headline md:text-lg text-white leading-none
                    transition-all ease-kibu duration-200 items-center cursor-pointer border border-2 border-transparent
                    hover:rounded-lg hover:brightness-105 hover:border-black/10 hover:scale-[1.02]`}
                >
                  Yes, close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomColoursContainer
